import type { PreferencesFormValues } from '../model/types'

import { identify, Identify } from '@amplitude/analytics-browser'
import { useQueryClient } from '@tanstack/react-query'
import { pickAll } from 'lodash/fp'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	usePreferencesMutation,
	usePreferencesQuery,
	userQueries,
} from '@/entities/user'

import { FormProvider } from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSeparator,
	FieldSet,
} from '@/shared/components/ui/field'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { Spinner } from '@/shared/components/ui/spinner'
import { useAppStore } from '@/shared/contexts/app'
import { getLangCode, useLocale } from '@/shared/i18n'
import i18n from '@/shared/i18n/client'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import { zodResolver } from '@/shared/lib/validator/zod'

import { preferencesFormSchema } from '../model/contract'

export function PreferencesForm() {
	const { t } = useTranslation()
	const locale = useLocale()
	const setDateFormat = useAppStore((state) => state.setDateFormat)
	const queryClient = useQueryClient()
	const preferencesMutation = usePreferencesMutation({
		onSuccess: (data, variables) => {
			setDateFormat(variables.date_format || 'EU')
			queryClient.invalidateQueries({
				queryKey: ['preferences'],
			})
		},
	})

	const preferences = usePreferencesQuery({
		select: (data: any): PreferencesFormValues => ({
			language: (data.language || locale) as 'en' | 'es' | 'de' | 'uk',
			date_format: (data.date_format || 'EU') as 'EU' | 'US',
		}),
	})

	const form = useForm({
		defaultValues: async () => {
			const data = await queryClient.query(userQueries.preferences())

			return {
				language: data.language || locale,
				date_format: data.date_format || 'EU',
			} as PreferencesFormValues
		},
		resolver: zodResolver(preferencesFormSchema),
	})
	if (preferences.isLoading) return <div>Loading...</div>
	const onSubmit = form.handleSubmit(async (data) => {
		i18n.changeLanguage(getLangCode(data.language))
		const identifyEvent = new Identify()
		identifyEvent.set('uiLocale', data.language)
		identifyEvent.set('dateFormat', data.date_format)
		identify(identifyEvent)
		ampli.preferencesUpdatePreferences()
		await toast.promise(
			preferencesMutation.mutateAsync(pickAll(['date_format'], data)),
			{
				loading: t('saving.proccess', { defaultValue: 'Saving...' }),
				success: t('saving.success', { defaultValue: 'Saved successfully' }),
				error: (error) => {
					Sentry.captureException(error)

					return t('saving.error', { defaultValue: 'Error while saving' })
				},
			}
		)
		form.reset(data)
	})

	return (
		<Card className='w-full max-w-4xl py-0'>
			<FormProvider methods={form} onSubmit={onSubmit}>
				<FieldSet disabled={preferencesMutation.isPending}>
					<FieldGroup className='px-4 pt-4'>
						<Controller
							name='language'
							control={form.control}
							render={({ field, fieldState }) => (
								<Field
									orientation='responsive'
									data-invalid={fieldState.invalid}
								>
									<FieldContent>
										<FieldLabel htmlFor='form-rhf-select-language'>
											{t('conf.language.label', { defaultValue: 'Language' })}
										</FieldLabel>
										<FieldDescription>
											{t('conf.language.description', {
												defaultValue: 'Select the language you want to use. ',
											})}
										</FieldDescription>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</FieldContent>
									<Select
										name={field.name}
										value={field.value}
										onValueChange={field.onChange}
									>
										<SelectTrigger
											id='form-rhf-select-language'
											aria-invalid={fieldState.invalid}
											className='min-w-[120px]'
										>
											<SelectValue placeholder='Select' />
										</SelectTrigger>
										<SelectContent position='item-aligned'>
											<SelectGroup>
												<SelectItem value='auto'>
													{t('conf.language.auto', { defaultValue: 'Auto' })}
												</SelectItem>
												<SelectSeparator />
												<SelectItem value='en'>
													{t('conf.language.en', { defaultValue: 'English' })}
												</SelectItem>
												<SelectItem value='es'>
													{t('conf.language.es', { defaultValue: 'Spanish' })}
												</SelectItem>
												<SelectItem value='de'>
													{t('conf.language.de', { defaultValue: 'German' })}
												</SelectItem>
												<SelectItem value='uk'>
													{t('conf.language.uk', { defaultValue: 'Ukrainian' })}
												</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</Field>
							)}
						/>
					</FieldGroup>
					<FieldSeparator />
					<FieldGroup className='px-4'>
						<Controller
							name='date_format'
							control={form.control}
							render={({ field, fieldState }) => (
								<Field
									orientation='responsive'
									data-invalid={fieldState.invalid}
								>
									<FieldContent>
										<FieldLabel htmlFor='form-rhf-select-language'>
											{t('conf.date_format.label', {
												defaultValue: 'Date format',
											})}
										</FieldLabel>
										<FieldDescription>
											{t('conf.date_format.description', {
												defaultValue:
													'Date format use for dates in the system.',
											})}
										</FieldDescription>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</FieldContent>
									<Select
										name={field.name}
										value={field.value}
										onValueChange={field.onChange}
									>
										<SelectTrigger
											id='form-rhf-select-date_format'
											aria-invalid={fieldState.invalid}
											className='min-w-[120px]'
										>
											<SelectValue placeholder='Select' />
										</SelectTrigger>
										<SelectContent position='item-aligned'>
											<SelectGroup>
												<SelectItem value='EU'>(EU) dd/mm/yyyy</SelectItem>
												<SelectItem value='US'>(US) mm/dd/yyyy</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</Field>
							)}
						/>
					</FieldGroup>
					<FieldSeparator />
					<FieldGroup className='px-4 pb-4'>
						<Field className='flex justify-end' orientation='responsive'>
							{form.formState.isDirty && (
								<Button
									type='button'
									variant='outline'
									onClick={() => form.reset()}
								>
									{t('general_cancel', { defaultValue: 'Cancel' })}
								</Button>
							)}
							<Button
								type='submit'
								variant='primary'
								disabled={
									preferencesMutation.isPending || !form.formState.isDirty
								}
							>
								{preferencesMutation.isPending && <Spinner />}
								{t('general_save_changes', { defaultValue: 'Save changes' })}
							</Button>
						</Field>
					</FieldGroup>
				</FieldSet>
			</FormProvider>
		</Card>
	)
}
